import { fmt, fmtPct } from '../../lib/nmtc/calculations';

const DEBT_TYPE_LABELS = {
  bank:  'Bank Financing',
  cdfi:  'CDFI Loan',
  other: 'Other Financing',
};

function SectionHeader({ label }) {
  return (
    <tr className="bg-gray-50">
      <td colSpan={3} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </td>
    </tr>
  );
}

function DataRow({ label, sublabel, amount, pct, variant = 'default', indent = false }) {
  const rowBg = {
    default:  'bg-white',
    alt:      'bg-gray-50',
    subtotal: 'bg-gray-100',
    total:    'bg-emerald-50',
    fee:      'bg-white',
  };
  const textCls = {
    default:  'text-gray-700',
    alt:      'text-gray-700',
    subtotal: 'text-gray-900 font-bold',
    total:    'text-emerald-900 font-bold',
    fee:      'text-gray-600',
  };

  return (
    <tr className={rowBg[variant]}>
      <td className={`px-4 py-2.5 text-sm ${textCls[variant]} ${indent ? 'pl-8' : ''}`}>
        {label}
        {sublabel && <span className="ml-2 text-xs text-gray-400 font-normal">{sublabel}</span>}
      </td>
      <td className={`px-4 py-2.5 text-sm text-right font-mono ${textCls[variant]}`}>
        {amount}
      </td>
      <td className={`px-4 py-2.5 text-sm text-right font-mono ${variant === 'total' ? 'text-emerald-700 font-bold' : 'text-gray-400'}`}>
        {pct ?? ''}
      </td>
    </tr>
  );
}

export default function SourcesUsesTable({ calc, inputs }) {
  const debtTypeLabel = DEBT_TYPE_LABELS[inputs.hardDebtType || 'bank'];
  const hasProjectCosts = (inputs.hardCosts ?? 0) > 0 || (inputs.softCosts ?? 0) > 0;
  const hardCosts = inputs.hardCosts ?? 0;
  const softCosts = inputs.softCosts ?? 0;
  const totalProjectCosts = hardCosts + softCosts;

  const totalUses = hasProjectCosts ? totalProjectCosts + calc.totalClosingFees : calc.qei;

  return (
    <div className="space-y-3">
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Line Item</th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 w-20">% of QEI</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            <SectionHeader label="Sources" />

            <DataRow
              label="Owner's Equity"
              sublabel="cash / equity contribution"
              amount={fmt(calc.ownerEquity)}
              pct={fmtPct(calc.ownerEquity / calc.qei)}
              variant="alt"
            />
            <DataRow
              label={debtTypeLabel}
              sublabel={`${inputs.hardDebtRate.toFixed(2)}% · ${inputs.loanTerm}yr`}
              amount={fmt(calc.hardDebt)}
              pct={fmtPct(calc.hardDebt / calc.qei)}
            />
            <DataRow
              label="Leverage Loan Subtotal"
              sublabel="A-Note to CDE"
              amount={fmt(calc.leverageLoan)}
              pct={fmtPct(calc.leverageLoan / calc.qei)}
              variant="subtotal"
            />
            <DataRow
              label="NMTC Tax Credit Equity"
              sublabel={`B-Note · $${inputs.taxCreditPrice.toFixed(2)}/credit · 1.0% I/O · forgiven Year 7`}
              amount={fmt(calc.nmtcEquity)}
              pct={fmtPct(calc.nmtcEquity / calc.qei)}
              variant="alt"
            />
            <DataRow
              label="Total QEI (Sources)"
              amount={fmt(calc.qei)}
              pct="100.0%"
              variant="total"
            />

            <SectionHeader label="Uses" />

            {hasProjectCosts && (
              <>
                {hardCosts > 0 && (
                  <DataRow
                    label="Hard Costs"
                    sublabel="construction / acquisition"
                    amount={fmt(hardCosts)}
                    pct={fmtPct(hardCosts / calc.qei)}
                    variant="alt"
                  />
                )}
                {softCosts > 0 && (
                  <DataRow
                    label="Soft Costs"
                    sublabel="architecture, engineering, permits"
                    amount={fmt(softCosts)}
                    pct={fmtPct(softCosts / calc.qei)}
                  />
                )}
                {hardCosts > 0 && softCosts > 0 && (
                  <DataRow
                    label="Total Project Costs"
                    amount={fmt(totalProjectCosts)}
                    pct={fmtPct(totalProjectCosts / calc.qei)}
                    variant="subtotal"
                  />
                )}
              </>
            )}

            <SectionHeader label="Closing Fees (paid at Year 0)" />

            <DataRow
              label="Sub-Allocation Fee"
              sublabel={`${inputs.subAllocationPct.toFixed(2)}% of QEI — paid to CDE for NMTC allocation`}
              amount={fmt(calc.subAllocationFee)}
              pct={fmtPct(calc.subAllocationFee / calc.qei)}
              variant="alt"
              indent
            />
            <DataRow
              label="New Markets Consultant Fee"
              sublabel="1.00% of QEI"
              amount={fmt(calc.nmtcConsultantFee)}
              pct={fmtPct(calc.nmtcConsultantFee / calc.qei)}
              indent
            />
            <DataRow
              label="Attorney Fees"
              sublabel="legal · structuring · closing"
              amount={fmt(calc.attorneyFees)}
              pct={fmtPct(calc.attorneyFees / calc.qei)}
              variant="alt"
              indent
            />
            <DataRow
              label="Accountant Fees"
              sublabel="NMTC accounting · tax opinion"
              amount={fmt(calc.accountantFees)}
              pct={fmtPct(calc.accountantFees / calc.qei)}
              indent
            />
            <DataRow
              label="Title Insurance"
              sublabel="~0.15% of leverage loan"
              amount={fmt(calc.titleInsurance)}
              pct={fmtPct(calc.titleInsurance / calc.qei)}
              variant="alt"
              indent
            />
            <DataRow
              label="Payment Performance Bond"
              sublabel="~0.75% of leverage loan"
              amount={fmt(calc.performanceBondFees)}
              pct={fmtPct(calc.performanceBondFees / calc.qei)}
              indent
            />
            <DataRow
              label="Total Closing Fees"
              amount={fmt(calc.totalClosingFees)}
              pct={fmtPct(calc.totalClosingFees / calc.qei)}
              variant="subtotal"
            />

            <tr className="bg-amber-50">
              <td className="px-4 py-2.5 text-sm text-amber-800" colSpan={3}>
                <span className="font-medium">+ Asset Management Fee (annual):</span>
                <span className="ml-2 text-amber-700">
                  {fmt(calc.assetMgmtFeeAnnual)}/yr × 7 years = {fmt(calc.assetMgmtFeeTotal)} nominal
                  <span className="text-xs ml-2">(NPV @ 5%: {fmt(calc.npvAssetMgmtFees)})</span>
                </span>
              </td>
            </tr>

            <DataRow
              label="Net Proceeds to Project"
              sublabel="QEI less closing fees"
              amount={fmt(calc.netProceedsToProject)}
              pct={fmtPct(calc.netProceedsToProject / calc.qei)}
              variant="alt"
            />
            <DataRow
              label="Total Uses"
              amount={fmt(hasProjectCosts ? totalUses : calc.qei)}
              pct="100.0%"
              variant="total"
            />
          </tbody>
        </table>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600 space-y-1.5">
        <p>
          Net proceeds represent{' '}
          <span className="font-medium text-gray-800">{fmtPct(calc.netProceedsToProject / calc.qei)}</span>{' '}
          of total QEI after estimated closing fees of{' '}
          <span className="font-medium text-gray-800">{fmt(calc.totalClosingFees)}</span>.
        </p>
        <p>
          Total fee burden (closing + 7-year asset management) is{' '}
          <span className="font-medium text-gray-800">{fmt(calc.totalFeesBurden)}</span> nominal{' '}
          ({fmtPct(calc.totalFeesBurden / calc.qei)} of QEI).
        </p>
        <p>
          The NMTC equity of{' '}
          <span className="font-medium text-gray-800">{fmt(calc.nmtcEquity)}</span>{' '}
          is forgiven at Year 7 — it converts to equity at unwind and does not represent a cash cost.
        </p>
      </div>
    </div>
  );
}
